import { useCallback, useEffect, useRef, useState } from 'react'
import type { VaultNotesApi } from '../LibraryDeps'

/** The grid and its thumbnails go first; notes are asked for after this. */
export const NOTES_START_MS = 500
/** Tiles that scroll into view within this window share one request. */
const NOTES_BATCH_MS = 150
/** The backend's cap per request (vault_notes.MAX_IDS). */
const NOTES_MAX_IDS = 200

/**
 * Operator notes for the tiles actually on screen, loaded after the grid:
 * nothing is asked for in the first NOTES_START_MS, then the ids of tiles that
 * scrolled into view are sent in small batches. A tile registers with
 * `observe(el)` (it carries `data-k` = the media id).
 */
export function useLazyNotes(api: VaultNotesApi | undefined, startMs = NOTES_START_MS) {
  const [notes, setNotes] = useState<Record<number, string>>({})
  const [badges, setBadges] = useState<Record<number, number[]>>({})
  const asked = useRef(new Set<number>())
  const pending = useRef(new Set<number>())
  const open = useRef(false)
  const timer = useRef(0)
  const io = useRef<IntersectionObserver | null>(null)
  const latest = useRef(api)
  latest.current = api

  const flush = useCallback(() => {
    const notesApi = latest.current
    if (!notesApi || !pending.current.size) return
    const ids = [...pending.current].slice(0, NOTES_MAX_IDS)
    ids.forEach((id) => pending.current.delete(id))
    notesApi
      .load(ids.map(String))
      .then((got) => {
        if (latest.current !== notesApi) return
        const add: Record<number, string> = {}
        for (const [id, text] of Object.entries(got.notes)) if (text) add[Number(id)] = text
        if (Object.keys(add).length) setNotes((n) => ({ ...n, ...add }))
        const tagged: Record<number, number[]> = {}
        for (const [id, ids] of Object.entries(got.badges ?? {})) if (ids.length) tagged[Number(id)] = ids
        if (Object.keys(tagged).length) setBadges((b) => ({ ...b, ...tagged }))
      })
      // Not loaded: ask again when these tiles are shown again.
      .catch(() => ids.forEach((id) => asked.current.delete(id)))
    if (pending.current.size) timer.current = window.setTimeout(flush, NOTES_BATCH_MS)
  }, [])

  const schedule = useCallback(() => {
    if (!open.current || timer.current) return
    timer.current = window.setTimeout(() => {
      timer.current = 0
      flush()
    }, NOTES_BATCH_MS)
  }, [flush])

  useEffect(() => {
    const start = window.setTimeout(() => {
      open.current = true
      schedule()
    }, startMs)
    return () => {
      window.clearTimeout(start)
      window.clearTimeout(timer.current)
      timer.current = 0
      open.current = false
      io.current?.disconnect()
      io.current = null
    }
  }, [startMs, schedule])

  const observe = useCallback(
    (el: Element | null) => {
      if (!el || !latest.current) return
      if (!io.current) {
        io.current = new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              if (!e.isIntersecting) continue
              io.current?.unobserve(e.target)
              const id = Number((e.target as HTMLElement).dataset.k)
              if (!Number.isFinite(id) || asked.current.has(id)) continue
              asked.current.add(id)
              pending.current.add(id)
            }
            schedule()
          },
          { rootMargin: '25% 0px' },
        )
      }
      io.current.observe(el)
    },
    [schedule],
  )

  return { notes, badges, observe }
}
